/* eslint-disable react-refresh/only-export-components -- the labels and cells the log and its drawer share */
import { ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { StatusTag } from '@/modules/settings';
import type { DeviationSentVia, DeviationWritingIssue } from '@/services/deviation.service';

/** Cells the Deviations log and its drawer both show. */

export const SENT_VIA_LABELS: Record<DeviationSentVia, string> = {
	workspace: 'Sentinel workspace',
	extension: 'Extension'
};

export const WRITING_KIND_LABELS: Record<DeviationWritingIssue['kind'], string> = {
	spelling: 'Spelling',
	grammar: 'Grammar',
	tone: 'Tone',
	formatting: 'Formatting'
};

/**
 * The ticket id, linked to the ticket in its helpdesk when there is a link (a new tab). A click on
 * the link does not also open the row's panel.
 */
export function TicketLink({ ticketId, ticketUrl, prefix = '' }: { ticketId: string; ticketUrl: string | null; prefix?: string }) {
	const label = `${prefix}#${ticketId}`;
	if (!ticketUrl) return <span className='truncate'>{label}</span>;
	return (
		<a
			href={ticketUrl}
			target='_blank'
			rel='noopener noreferrer'
			onClick={(e) => e.stopPropagation()}
			onKeyDown={(e) => e.stopPropagation()}
			title='Open the ticket in the helpdesk'
			className='inline-flex min-w-0 items-center gap-1 text-ink underline-offset-2 hover:underline focus-visible:underline focus-visible:outline-none'
		>
			<span className='truncate'>{label}</span>
			<ExternalLink aria-hidden='true' className='size-3 shrink-0 text-gray-800' />
		</a>
	);
}

/** The score against the brand's threshold, in red under it; a dash when the AI check did not score the reply. */
export function AiScoreCell({ aiScore, blockBelow }: { aiScore: number | null; blockBelow: number }) {
	if (aiScore == null) {
		return (
			<span className='flex flex-col'>
				<span className='text-gray-700'>—</span>
				<span className='text-[11px] leading-4 text-gray-800'>AI skipped</span>
			</span>
		);
	}
	return (
		<span className='tabular-nums'>
			<span className={cn('font-medium', aiScore < blockBelow ? 'text-red-600' : 'text-ink')}>{aiScore.toFixed(1)}</span>
			<span className='text-[12px] text-gray-800'> / {blockBelow}</span>
		</span>
	);
}

/** One tag per kind of writing issue, with how many there are. */
export function WritingIssueTags({ issues }: { issues: DeviationWritingIssue[] }) {
	if (!issues.length) return <span className='text-gray-700'>—</span>;
	const counts = issues.reduce<Partial<Record<DeviationWritingIssue['kind'], number>>>(
		(acc, issue) => ({ ...acc, [issue.kind]: (acc[issue.kind] ?? 0) + 1 }),
		{}
	);
	return (
		<span className='flex flex-wrap gap-1'>
			{Object.entries(counts).map(([kind, count]) => (
				<StatusTag key={kind} tone='amber'>
					{WRITING_KIND_LABELS[kind as DeviationWritingIssue['kind']]}
					{count > 1 ? ` ${count}` : ''}
				</StatusTag>
			))}
		</span>
	);
}
